import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = {
  daily: "#8B5CF6",
  weekly: "#EC4899",
  monthly: "#1FAF5C",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className='bg-white rounded-xl border border-border-subtle shadow-lg px-4 py-3'>
      <p className='text-body-xs font-semibold text-text-primary mb-1.5'>{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className='flex items-center gap-2 text-body-xs'>
          <span className='w-2 h-2 rounded-full' style={{ backgroundColor: entry.color }} />
          <span className='text-text-secondary'>{entry.name}:</span>
          <span className='font-semibold text-text-primary'>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const ActivityTrendsChart = ({ data, isLoading, error, height = 400, hideMonthlyMau = false }) => {
  const boxStyle = { height };
  if (isLoading) {
    return (
      <div className='flex items-center justify-center' style={boxStyle}>
        <div className='flex flex-col items-center gap-2'>
          <div className='animate-spin rounded-full h-6 w-6 border-2 border-accent-purple border-t-transparent' />
          <span className='text-body-sm text-text-tertiary'>Loading trends...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center' style={boxStyle}>
        <p className='text-body-sm text-severity-high'>Failed to load activity trends</p>
      </div>
    );
  }

  const hasSeries =
    (data?.daily_users?.length ?? 0) > 0 ||
    (data?.weekly_users?.length ?? 0) > 0 ||
    (!hideMonthlyMau && (data?.monthly_users?.length ?? 0) > 0);

  if (!data || !hasSeries) {
    return (
      <div className='flex items-center justify-center' style={boxStyle}>
        <p className='text-body-sm text-text-tertiary'>No activity data available</p>
      </div>
    );
  }

  const dateMap = new Map();
  const addData = (items, key) => {
    items?.forEach((item) => {
      if (!dateMap.has(item.date)) dateMap.set(item.date, { date: item.date });
      dateMap.get(item.date)[key] = item.active_users;
    });
  };
  addData(data.daily_users, "dailyUsers");
  addData(data.weekly_users, "weeklyUsers");
  if (!hideMonthlyMau) {
    addData(data.monthly_users, "monthlyUsers");
  }

  const chartData = Array.from(dateMap.keys())
    .sort()
    .map((d) => {
      const row = dateMap.get(d);
      return {
        date: d,
        dailyUsers: row.dailyUsers ?? 0,
        weeklyUsers: row.weeklyUsers ?? 0,
        ...(hideMonthlyMau ? {} : { monthlyUsers: row.monthlyUsers ?? 0 }),
      };
    });

  return (
    <div style={boxStyle}>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(10,16,48,0.06)' />
          <XAxis
            dataKey='date'
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={{ stroke: "rgba(10,16,48,0.08)" }}
            tickLine={false}
          />
          <YAxis
            domain={[0, "auto"]}
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconType='circle'
            iconSize={8}
            wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
          />
          {/* linear: one backend point per calendar day; avoids monotone spline artifacts across gaps */}
          <Line type='linear' dataKey='dailyUsers' stroke={COLORS.daily} strokeWidth={2} dot={false} name='DAU' />
          <Line type='linear' dataKey='weeklyUsers' stroke={COLORS.weekly} strokeWidth={2} dot={false} name='WAU (7d)' />
          {!hideMonthlyMau && (
            <Line type='linear' dataKey='monthlyUsers' stroke={COLORS.monthly} strokeWidth={2} dot={false} name='MAU (30d)' />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ActivityTrendsChart;
